import React, { useState } from 'react';

import { useSnackbar } from 'notistack';
import { useDropzone } from 'react-dropzone';

import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { Box, LinearProgress, Stack, Typography } from '@mui/material';

import { ALLOWED_IMAGE_MIMETYPES, getUploadedImageId, uploadImage } from '~/services/ImageService';

import getErrorMessage from '~/utils/getErrorMessage';

interface Props {
  /** How many more images fit. */
  room: number;
  onUploaded: (svcImageIds: Array<string>) => void;
}

/** Drop or pick several images at once; each is uploaded in turn and added in the order picked. */
export default function NativeTourImageDropzone({ room, onUploaded }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const upload = async (files: Array<File>) => {
    const picked = files.slice(0, room);
    if (files.length > room) {
      enqueueSnackbar(`Only ${room} more fit, so the first ${room} are uploaded.`, { variant: 'warning' });
    }
    const ids: Array<string> = [];
    setProgress({ done: 0, total: picked.length });
    for (const file of picked) {
      try {
        const id = getUploadedImageId(await uploadImage(file));
        if (id) {
          ids.push(id);
        }
      } catch (error) {
        enqueueSnackbar(getErrorMessage(error, `Couldn't upload ${file.name}`), { variant: 'error' });
      }
      setProgress({ done: ids.length, total: picked.length });
    }
    setProgress(null);
    if (ids.length > 0) {
      onUploaded(ids);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: Object.fromEntries(ALLOWED_IMAGE_MIMETYPES.map((type) => [type, []])),
    multiple: true,
    disabled: room <= 0 || progress !== null,
    onDrop: (accepted, rejected) => {
      if (rejected.length > 0) {
        enqueueSnackbar(`Not images: ${rejected.map(({ file }) => file.name).join(', ')}`, { variant: 'warning' });
      }
      if (accepted.length > 0) {
        upload(accepted);
      }
    },
  });

  if (room <= 0) {
    return null;
  }
  return (
    <Box
      {...getRootProps()}
      sx={{
        p: 2,
        border: 1,
        borderStyle: 'dashed',
        borderColor: isDragActive ? 'primary.main' : 'divider',
        borderRadius: 1,
        bgcolor: isDragActive ? 'action.selected' : 'action.hover',
        cursor: progress ? 'progress' : 'pointer',
      }}
    >
      <input {...getInputProps()} />
      {progress ? (
        <Stack spacing={1}>
          <Typography variant="body2">
            Uploading {progress.done + 1 > progress.total ? progress.total : progress.done + 1} of {progress.total}…
          </Typography>
          <LinearProgress variant="determinate" value={(progress.done / progress.total) * 100} />
        </Stack>
      ) : (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CloudUploadOutlinedIcon color="action" />
          <Typography variant="body2" color="text.secondary">
            Drop images here, or click to pick several (up to {room} more)
          </Typography>
        </Stack>
      )}
    </Box>
  );
}
